import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  CheckSquare, Plus, Loader2, User, Calendar, ChevronRight, ListTodo,
  MessageSquare,
} from 'lucide-react';

const STATUS_LABELS = {
  todo: { label: 'À faire', color: 'bg-slate-100 text-slate-700' },
  in_progress: { label: 'En cours', color: 'bg-amber-100 text-amber-700' },
  done: { label: 'Terminé', color: 'bg-emerald-100 text-emerald-700' },
  archived: { label: 'Archivée', color: 'bg-slate-50 text-slate-400' },
};
const PRIORITY_LABELS = {
  low: { label: 'Basse', color: 'border-slate-200 text-slate-500' },
  medium: { label: 'Moy.', color: 'border-blue-200 text-blue-700' },
  high: { label: 'Haute', color: 'border-amber-300 text-amber-700' },
  urgent: { label: 'Urgente', color: 'border-red-300 text-red-700' },
};

/**
 * Onglet « Tâches liées » et bouton « Discuter » à inclure dans
 * n'importe quelle fiche d'entité (chantier, devis, facture…).
 *
 * Props:
 * - entityType : "chantier" | "devis" | "facture" | "facture_achat" | "contact" | "gestion_client"
 * - entityId   : id de l'entité
 * - entityLabel: libellé pour l'auto-création
 */
export default function EntityTasksPanel({ entityType, entityId, entityLabel }) {
  const navigate = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({
    title: '', description: '', priority: 'medium', due_date: '', assignee_user_id: '',
  });

  const load = async () => {
    if (!entityId) return;
    setLoading(true);
    try {
      const r = await api.get('/tasks', {
        params: { entity_type: entityType, entity_id: entityId },
      });
      setTasks(r.data?.tasks || []);
    } catch (e) {
      // silent
    }
    setLoading(false);
  };
  const loadUsers = async () => {
    try {
      const r = await api.get('/users/list-slim');
      setUsers(r.data?.users || []);
    } catch (_) {}
  };
  useEffect(() => { load(); loadUsers(); /* eslint-disable-next-line */ }, [entityType, entityId]);

  const handleCreate = async () => {
    if (!form.title.trim()) {
      toast.error('Le titre est requis');
      return;
    }
    setCreating(true);
    try {
      await api.post('/tasks', {
        title: form.title.trim(),
        description: form.description || null,
        priority: form.priority,
        due_date: form.due_date || null,
        assignee_user_id: form.assignee_user_id || null,
        entity_type: entityType,
        entity_id: entityId,
        entity_label: entityLabel || null,
      });
      toast.success('Tâche créée');
      setShowCreate(false);
      setForm({ title: '', description: '', priority: 'medium', due_date: '', assignee_user_id: '' });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur lors de la création');
    }
    setCreating(false);
  };

  const userName = (uid) => {
    if (!uid) return null;
    const u = users.find((x) => x.user_id === uid);
    return u?.name || u?.email || uid;
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-4" data-testid={`entity-tasks-${entityType}-${entityId}`}>
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
          <ListTodo className="w-5 h-5 text-blue-600" />
          Tâches liées
          {tasks.length > 0 && (
            <Badge className="bg-blue-100 text-blue-700">{tasks.length}</Badge>
          )}
        </h2>
        <div className="flex items-center gap-2">
          <Button
            type="button" variant="outline" size="sm"
            onClick={() => navigate(`/discussions?type=entity&entityType=${entityType}&entityId=${entityId}`)}
            data-testid={`entity-chat-btn-${entityType}-${entityId}`}
            className="border-purple-200 text-purple-700 hover:bg-purple-50"
          >
            <MessageSquare className="w-3.5 h-3.5 mr-1" /> Discuter
          </Button>
          <Button
            type="button" size="sm"
            onClick={() => setShowCreate((v) => !v)}
            className="bg-blue-600 hover:bg-blue-700"
            data-testid={`entity-task-add-${entityType}-${entityId}`}
          >
            <Plus className="w-3.5 h-3.5 mr-1" /> Ajouter
          </Button>
        </div>
      </div>

      {loading && (
        <div className="py-4 text-center" key="ent-tasks-loading">
          <Loader2 className="w-5 h-5 animate-spin mx-auto text-slate-400" />
        </div>
      )}

      {!loading && tasks.length === 0 && !showCreate && (
        <p key="ent-tasks-empty" className="text-sm text-slate-500 text-center py-3">
          Aucune tâche liée. Cliquez sur « Ajouter » pour en créer une.
        </p>
      )}

      {!loading && tasks.length > 0 && (
        <div className="divide-y divide-slate-100 -mx-2" data-testid="entity-tasks-list">
          {tasks.map((t) => {
            const st = STATUS_LABELS[t.status] || STATUS_LABELS.todo;
            const pr = PRIORITY_LABELS[t.priority] || PRIORITY_LABELS.medium;
            return (
              <button
                key={t.task_id}
                type="button"
                onClick={() => navigate(`/taches/${t.task_id}`)}
                className="w-full text-left px-2 py-2.5 hover:bg-slate-50 rounded flex items-center gap-3"
                data-testid={`ent-task-${t.task_id}`}
              >
                <CheckSquare className="w-4 h-4 text-slate-400 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-medium text-slate-900 truncate">{t.title}</span>
                    <Badge className={st.color + ' text-[10px]'}>{st.label}</Badge>
                    <Badge variant="outline" className={pr.color + ' text-[10px]'}>{pr.label}</Badge>
                  </div>
                  <div className="flex items-center gap-3 mt-0.5 text-[11px] text-slate-500">
                    {t.assignee_user_id && (
                      <span className="flex items-center gap-1">
                        <User className="w-3 h-3" /> {userName(t.assignee_user_id)}
                      </span>
                    )}
                    {t.due_date && (
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" /> {t.due_date}
                      </span>
                    )}
                  </div>
                </div>
                <ChevronRight className="w-4 h-4 text-slate-400 shrink-0" />
              </button>
            );
          })}
        </div>
      )}

      {/* Mini formulaire inline */}
      {showCreate && (
        <div key="ent-tasks-create" className="bg-slate-50 border border-slate-200 rounded-lg p-3 space-y-2" data-testid="entity-task-create-form">
          <Input
            placeholder="Titre de la tâche *"
            value={form.title}
            onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
            data-testid="ent-task-title"
            autoFocus
          />
          <textarea
            placeholder="Description (facultatif)"
            value={form.description}
            onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
            rows={2}
            className="w-full rounded-md border border-slate-200 px-3 py-1.5 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            data-testid="ent-task-desc"
          />
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <select
              value={form.priority}
              onChange={(e) => setForm((f) => ({ ...f, priority: e.target.value }))}
              className="h-9 rounded-md border border-slate-200 px-2 text-sm bg-white"
              data-testid="ent-task-priority"
            >
              <option value="low">Basse</option>
              <option value="medium">Moyenne</option>
              <option value="high">Haute</option>
              <option value="urgent">Urgente</option>
            </select>
            <Input
              type="date"
              value={form.due_date}
              onChange={(e) => setForm((f) => ({ ...f, due_date: e.target.value }))}
              data-testid="ent-task-due"
            />
            <select
              value={form.assignee_user_id}
              onChange={(e) => setForm((f) => ({ ...f, assignee_user_id: e.target.value }))}
              className="h-9 rounded-md border border-slate-200 px-2 text-sm bg-white"
              data-testid="ent-task-assignee"
            >
              <option value="">— Personne —</option>
              {users.map((u) => (
                <option key={u.user_id} value={u.user_id}>{u.name || u.email}</option>
              ))}
            </select>
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <Button variant="outline" size="sm" onClick={() => setShowCreate(false)} disabled={creating}>
              Annuler
            </Button>
            <Button size="sm" onClick={handleCreate} disabled={creating} data-testid="ent-task-submit">
              {creating
                ? <Loader2 key="ent-cr-load" className="w-3.5 h-3.5 mr-1 animate-spin" />
                : <Plus key="ent-cr-icon" className="w-3.5 h-3.5 mr-1" />}
              Créer
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
